import { useState } from 'react';
import { ChevronLeft, Bell, Mail, Smartphone, MessageSquare, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import supabase from '../../lib/supabase';
import type { Database } from '../../types/database.types';

interface NotificationsScreenProps {
  onNavigate: (screen: any) => void;
}

type NotificationPreferences = Record<string, boolean> & {
  email_bookings: boolean;
  email_promotions: boolean;
  email_updates: boolean;
  sms_bookings: boolean;
  sms_promotions: boolean;
  sms_updates: boolean;
  push_bookings: boolean;
  push_promotions: boolean;
  push_updates: boolean;
};

export default function NotificationsScreen({ onNavigate }: NotificationsScreenProps) {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [preferences, setPreferences] = useState<NotificationPreferences>({
    email_bookings: true,
    email_promotions: true,
    email_updates: false,
    sms_bookings: true,
    sms_promotions: false,
    sms_updates: false,
    push_bookings: true,
    push_promotions: true,
    push_updates: true,
  });

  const handleToggle = (key: keyof NotificationPreferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    if (!user) {
      setError('Please log in to save preferences');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Save to profiles table or separate notification_preferences table
      const row: Database['public']['Tables']['profiles']['Insert'] = {
        user_id: user.id,
        notification_preferences: preferences,
      };

      await supabase
        .from('profiles')
        .upsert(row);

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const notificationTypes = [
    { key: 'bookings', label: 'Booking Updates', description: 'Confirmations, reminders, and changes' },
    { key: 'promotions', label: 'Promotions & Offers', description: 'Special deals and discounts' },
    { key: 'updates', label: 'Product Updates', description: 'New features and improvements' },
  ];

  const channels = [
    { key: 'email', label: 'Email', icon: Mail, color: 'text-blue-600' },
    { key: 'sms', label: 'SMS', icon: MessageSquare, color: 'text-green-600' },
    { key: 'push', label: 'Push', icon: Bell, color: 'text-[#1A6B4F]' },
  ];

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('profile')}
            className="w-10 h-10 rounded-full hover:bg-secondary transition-colors flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg">Notifications</h1>
            <p className="text-xs text-muted-foreground">Manage your preferences</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-[#D4F04A]/10 border border-[#D4F04A]/20 rounded-xl p-4 text-sm text-[#1A6B4F]">
            Preferences saved successfully!
          </div>
        )}

        <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] rounded-2xl p-6 text-white">
          <Bell className="w-12 h-12 mb-3 opacity-80" />
          <h2 className="text-lg mb-2">Stay Updated</h2>
          <p className="text-sm opacity-90">
            Choose how you want to receive notifications about your bookings, promotions, and updates.
          </p>
        </div>

        <div>
          <h3 className="text-base mb-3">Notification Channels</h3>
          <div className="space-y-4">
            {notificationTypes.map((type) => (
              <div key={type.key} className="bg-card border border-border rounded-2xl p-4">
                <div className="mb-3">
                  <div className="text-sm font-medium mb-1">{type.label}</div>
                  <div className="text-xs text-muted-foreground">{type.description}</div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {channels.map((channel) => {
                    const Icon = channel.icon;
                    const prefKey = `${channel.key}_${type.key}` as keyof NotificationPreferences;
                    const isEnabled = preferences[prefKey];

                    return (
                      <button
                        key={channel.key}
                        onClick={() => handleToggle(prefKey)}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          isEnabled
                            ? 'border-[#1A6B4F] bg-[#D4F04A]/10'
                            : 'border-border bg-secondary hover:border-[#1A6B4F]/30'
                        }`}
                      >
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${isEnabled ? channel.color : 'text-muted-foreground'}`} />
                        <div className={`text-xs ${isEnabled ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {channel.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-secondary rounded-xl p-4">
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-[#1A6B4F]" />
            Contact Information
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email</span>
              <span>{user?.email || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Phone</span>
              <span>{user?.user_metadata?.phone || 'Not set'}</span>
            </div>
          </div>
          <button
            onClick={() => onNavigate('personal-info')}
            className="mt-3 text-xs text-[#1A6B4F] hover:underline"
          >
            Update contact information
          </button>
        </div>

        <div className="bg-secondary rounded-xl p-4 text-sm text-muted-foreground">
          <p className="leading-relaxed">
            <strong className="text-foreground">Note:</strong> Booking confirmations and important updates will always be sent via email regardless of your preferences to ensure you don't miss critical information.
          </p>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border p-4 pb-24">
        <button
          onClick={handleSave}
          disabled={loading}
          className="w-full h-14 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Preferences'
          )}
        </button>
      </div>
    </div>
  );
}
